import { useState } from "react";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import $api from "@/frontend/shared/api/axios";
import { Input } from "@/frontend/shared/ui/Input";
import { NormalButton } from "@/frontend/shared/ui/Button";
import { Typography } from "@/frontend/shared/ui/Typography";
import { snackbarConstructor } from "@/frontend/shared/ui/CustomNotifications/ui/Snackbar/snackbarConstructor";
import { reportErrorToSentry } from "@/frontend/shared/lib/reportErrorToSentry/reportErrorToSentry";
import { InnoveraLoginHeader } from "@/frontend/components/InnoveraLoginHeader/ui/InnoveraLoginHeader";
import { CaretLeftIcon } from "@/frontend/shared/assets/icons";
import { useHistoricalNavigate } from "@/frontend/shared/lib/useHistoricalNavigate/useHistoricalNavigate";
import styles from "./ForgotPasswordPage.module.scss";
import { LogoSvg } from "@/frontend/shared/assets/LogoSvg";

const EmailSchema = z.string().email();

export function ForgotPasswordPage() {
  const [email, setEmail] = useState<string>("");
  const isValidEmailInput = EmailSchema.safeParse(email).success;
  const { stepBack } = useHistoricalNavigate();

  const mutation = useMutation({
    mutationFn: async ({ email }: { email: string }) => {
      const response = await $api.post("/auth/passwords/forgot", { email });
      return response.data;
    },
    onSuccess: () => {
      snackbarConstructor(
        `If an Innovera account exists for ${email}, an email will be sent with further instructions.`,
        "normal"
      );
      setEmail("");
    },
    onError: (err) => {
      const message = err?.message ?? "Failed to retrieve password reset link";
      snackbarConstructor(message, "error");
      reportErrorToSentry(err, "FORGOT PASSWORD: /auth/passwords/forgot");
    }
  });

  const onChangeSetEmail = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setEmail(e.target.value);
  };

  const forgotPassword = async () => {
    await mutation.mutateAsync({ email });
  };

  return (
    <div className={styles.container}>
      {/* <InnoveraLoginHeader className={styles.header} /> */}
      <LogoSvg className={styles.logo} />
      <div className={styles.formWrapper}>
        {/* <NormalButton size="large" variant="tertiary" onClick={() => stepBack()} className={styles.backButton}>
          <CaretLeftIcon style={{ fill: "var(--greyscale700)" }} />
          <Typography variant="label-4" color="greyscale700">
            Back to Log in
          </Typography>
        </NormalButton> */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            forgotPassword();
          }}
        >
          <div className={styles.form}>
            <div className={styles.heading}>
              <Typography variant="heading-6" color="greyscale900">
                Forgot Password?
              </Typography>
              <Typography variant="body-2" color="greyscale700">
                Enter your email address and we will send you instructions to reset your password.
              </Typography>
            </div>
            <div className={styles.inputContainer}>
              <Typography variant="label-4" color="greyscale700">
                Email
              </Typography>
              <Input type="text" value={email} onChange={onChangeSetEmail} inputSize="small" />
            </div>
          </div>
          <NormalButton
            className={styles.submitButton}
            size="large"
            isDisabled={!email || !isValidEmailInput}
            isSubmitting={mutation.isPending}
            type="submit"
          >
            Reset Password
          </NormalButton>
        </form>
      </div>
    </div>
  );
}
